import { useEffect, useState, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    broadcast,
    GroupChatAvatarImg,
    ImgUserPlaceholder,
    loaderSVG,
    SampleChatProfile,
    SampleGroupProfile,
    SingleChatAvatarImg2,
} from '../../../../assets/images';
import { isSingleChat } from '../../../../Helpers/Chat/ChatHelper';
import IndexedDb from '../../../../Helpers/IndexedDb';
import { getColorCodeInitials, getInitialsFromName, isPhoneNumber } from '../../../../Helpers/Utility';
import { cn } from '../../../../ui/lib/utils';
import SvgProfileName from '../SvgProfileName';
import { scheduleAvatarLoad } from './avatarLoadScheduler';

const avatarIconObject = {
    chat: SampleChatProfile,
    groupchat: SampleGroupProfile,
    broadcast: broadcast,
    loginProfile: ImgUserPlaceholder,
};

const CHAT_TYPES = {
    GROUP_CHAT: 'groupchat',
};

const computeInitialsData = (chatType, name, blocked) => {
    if (isSingleChat(chatType) && name && !blocked && !isPhoneNumber(name)) {
        const initialName = getInitialsFromName(name);
        if (initialName) {
            return {
                show: true,
                colorCode: getColorCodeInitials(name),
                initial: initialName,
            };
        }
    }
    return { show: false, colorCode: '', initial: '' };
};

function useAvatarImage({ imageToken, imageType, fileKey, blocked, localDb }) {
    const [url, setUrl] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        setUrl(null);
        setError(false);

        if (blocked || !imageToken) return;

        if (/^blob:http/i.test(imageToken)) {
            setUrl(imageToken);
            return;
        }

        let cancelled = false;
        let blobUrl = null;

        scheduleAvatarLoad(async () => {
            const blob = await localDb.getImageByKey(imageToken, imageType, fileKey);

            if (cancelled) return;

            blobUrl = URL.createObjectURL(blob);

            const img = new Image();
            img.src = blobUrl;
            await img.decode();

            if (!cancelled) {
                setUrl(img.src);
            }
        }).catch(() => {
            if (!cancelled) {
                setError(true);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [imageToken, imageType, fileKey, blocked, localDb]);

    return { url, error };
}

const ImageComponent = memo(
    ({
        imageToken,
        fileKey = '',
        blocked = false,
        chatType,
        getImageUrl,
        onclickHandler,
        imageType = 'profileimages',
        name = '',
        hideClassNameStatus = false,
        ...props
    }) => {
        const localDb = useMemo(() => new IndexedDb(), []);

        const avatarIcon = avatarIconObject[chatType] || avatarIconObject.chat;

        const { url, error } = useAvatarImage({
            imageToken,
            imageType,
            fileKey,
            blocked,
            localDb,
        });

        const initialsData = useMemo(() => computeInitialsData(chatType, name, blocked), [chatType, name, blocked]);

        const showInitials = !imageToken && initialsData.show;
        const finalUrl = url || avatarIcon;
        useEffect(() => {
            if (getImageUrl && typeof getImageUrl === 'function') {
                getImageUrl(finalUrl);
            }
        }, [finalUrl, getImageUrl]);

        const handleKeyDown = e => {
            if ((e.key === 'Enter' || e.key === ' ') && finalUrl !== avatarIcon) {
                onclickHandler?.(e);
            }
        };

        if (imageToken) {
            return (
                <img
                    src={finalUrl}
                    data-jest-id="jestImageComponent"
                    alt=""
                    className={cn(props.classProps, props.caption === '' && 'no-caption', error && 'cursor-default')}
                    style={{
                        backgroundImage: `url(${loaderSVG})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        backgroundSize: 'contain',
                        minWidth: hideClassNameStatus ? '' : '50px',
                        minHeight: hideClassNameStatus ? '' : '50px',
                    }}
                    onError={e => {
                        e.currentTarget.src = avatarIcon;
                    }}
                    onClick={finalUrl !== avatarIcon ? onclickHandler : null}
                    onKeyDown={handleKeyDown}
                    role="button"
                    tabIndex={finalUrl !== avatarIcon ? 0 : -1}
                    loading="lazy"
                    decoding="async"
                    fetchpriority="low"
                />
            );
        }

        if (chatType === CHAT_TYPES.GROUP_CHAT) {
            return <GroupChatAvatarImg />;
        }

        if (showInitials && !blocked && !isPhoneNumber(name)) {
            return (
                <SvgProfileName
                    userShortName={initialsData.initial}
                    initialColor={initialsData.colorCode}
                    hideClassNameStatus={hideClassNameStatus}
                />
            );
        }

        return <SingleChatAvatarImg2 />;
    },
);

ImageComponent.displayName = 'ImageComponent';

export default ImageComponent;

ImageComponent.propTypes = {
    imgSrc: PropTypes.string,
    handleMediaShow: PropTypes.func,
    isSender: PropTypes.bool,
    uploadStatus: PropTypes.number,
    messageObject: PropTypes.object,
    imgFileDownloadOnclick: PropTypes.func,
    imageHeightAdjust: PropTypes.bool,
    pageType: PropTypes.string,
    imageUrlDownloaded: PropTypes.string,
    userToken: PropTypes.string,
    imageToken: PropTypes.string,
    fileKey: PropTypes.string,
    blocked: PropTypes.bool,
    chatType: PropTypes.oneOf(['chat', 'groupchat', 'broadcast', 'loginProfile']),
    temporary: PropTypes.bool,
    getImageUrl: PropTypes.func,
    onclickHandler: PropTypes.func,
    imageType: PropTypes.string,
    name: PropTypes.string,
    classProps: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    caption: PropTypes.string,
};
